// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {FaTrash, FaEdit} from 'react-icons/fa';
import {Navigate} from 'react-router-dom';
import {useAuth} from '../../hooks/useAuth';
import {getAdminNews, createNews, updateNews, deleteNews, uploadNewsImage, getAvailableImages} from '../../api/axios';

const Container = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 20px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Input = styled.input`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
`;

const TextAreaShort = styled.textarea`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
  height: 50px;
`;

const TextArea = styled.textarea`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
  height: 100px;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 5px;
  color: white;
  cursor: pointer;
`;

const ButtonTable = styled.button`
  background: transparent;
  height: 20px;
  width: 20px;
  margin-right: 10px;
  border-radius: 5px;
  color: white;
  cursor: pointer;
  padding: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
`;

const ImagePreview = styled.img`
  width: 100px;
  height: auto;
  margin: 10px;
  cursor: pointer;
`;

const ImageList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
`;

const TableHeader = styled.th`
  border: 1px solid #ddd;
  padding: 10px;
  text-align: center;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #ddd;
  &:nth-child(even) {
    background-color: rgb(65, 10, 10);
  }
`;

const TableData = styled.td`
  border: 1px solid #ddd;
  padding: 10px;
  text-align: center;
`;

const AdminNewsPanel = () => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const [news, setNews] = useState([]);
  const [editingNews, setEditingNews] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Announcement');
  const [shortDescription, setShortDescription] = useState('');
  const [fullContent, setFullContent] = useState('');
  const [image, setImage] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [setSelectedFile] = useState(null);
  const [availableImages, setAvailableImages] = useState([]);

  useEffect(() => {
    fetchNews();
    fetchAvailableImages();
  }, []);

  if (!user || user.permission !== 3) {
    return <Navigate to="/" replace />;
  }

  const fetchNews = async () => {
    try {
      const response = await getAdminNews();
      setNews(response.data);
    } catch (error) {
      throw new Error(`${error}`);
    }
  };

  const fetchAvailableImages = async () => {
    try {
      const response = await getAvailableImages();
      if (response.status !== 200) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      setAvailableImages(response.data);
    } catch (error) {
      throw new Error(`${error}`);
    }
  };

  const handleImageUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);

    const formData = new FormData();
    formData.append('image', file);
    try {
      const response = await uploadNewsImage(formData);
      setImage(response.data.imageUrl);
      fetchAvailableImages();
    } catch (error) {
      throw new Error(`${error}`);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const newsData = {title, category, shortDescription, fullContent, image, isPublished};

    if (editingNews) {
      newsData.id = editingNews.id;
      await updateNews(newsData);
    } else {
      await createNews(newsData);
    }

    resetForm();
    fetchNews();
  };

  const handleEdit = newsItem => {
    setEditingNews(newsItem);
    setTitle(newsItem.title);
    setCategory(newsItem.category);
    setShortDescription(newsItem.shortDescription);
    setFullContent(newsItem.fullContent);
    setImage(newsItem.image);
    setIsPublished(newsItem.isPublished);
  };

  const handleDelete = async id => {
    if (window.confirm(t('adminNewsPanel.confirmDelete'))) {
      await deleteNews(id);
      fetchNews();
    }
  };

  const resetForm = () => {
    setEditingNews(null);
    setTitle('');
    setCategory('Announcement');
    setShortDescription('');
    setFullContent('');
    setImage('');
    setIsPublished(false);
  };

  return (
    <Container>
      <h2>{t('adminNewsPanel.title')}</h2>
      <Form onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder={t('adminNewsPanel.titlePlaceholder')}
          value={title}
          onChange={e => setTitle(e.target.value)}
          required
        />
        <select value={category} onChange={e => setCategory(e.target.value)}>
          <option value="Announcement">{t('adminNewsPanel.category.announcement')}</option>
          <option value="Update">{t('adminNewsPanel.category.update')}</option>
          <option value="Event">{t('adminNewsPanel.category.event')}</option>
          <option value="Maintenance">{t('adminNewsPanel.category.maintenance')}</option>
        </select>
        <Input type="file" accept="image/*" onChange={handleImageUpload} />
        {image && <ImagePreview src={image} alt="Preview" />}
        <h4>{t('adminNewsPanel.selectExistingImage')}</h4>
        <ImageList>
          {availableImages.map(img => (
            <ImagePreview key={img} src={img} alt={img} onClick={() => setImage(img)} />
          ))}
        </ImageList>
        <TextAreaShort
          placeholder={t('adminNewsPanel.shortDescription')}
          value={shortDescription}
          onChange={e => setShortDescription(e.target.value)}
          required
        />
        <TextArea
          placeholder={t('adminNewsPanel.fullContent')}
          value={fullContent}
          onChange={e => setFullContent(e.target.value)}
          required
        />
        <label>
          <input type="checkbox" checked={isPublished} onChange={() => setIsPublished(!isPublished)} />{' '}
          {t('adminNewsPanel.publish')}
        </label>
        <ButtonRow>
          <Button type="submit">{editingNews ? t('adminNewsPanel.updateNews') : t('adminNewsPanel.createNews')}</Button>
          <Button type="button" onClick={resetForm}>
            {t('adminNewsPanel.reset')}
          </Button>
        </ButtonRow>
      </Form>
      <h3>{t('adminNewsPanel.existingNews')}</h3>
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <TableHeader>{t('adminNewsPanel.table.title')}</TableHeader>
              <TableHeader>{t('adminNewsPanel.table.category')}</TableHeader>
              <TableHeader>{t('adminNewsPanel.table.date')}</TableHeader>
              <TableHeader>{t('adminNewsPanel.table.public')}</TableHeader>
              <TableHeader>{t('adminNewsPanel.table.creator')}</TableHeader>
              <TableHeader>{t('adminNewsPanel.table.actions')}</TableHeader>
            </tr>
          </thead>
          <tbody>
            {news.map(item => (
              <TableRow key={item.id}>
                <TableData>{item.title}</TableData>
                <TableData>{item.category.substr(0, 5)}</TableData>
                <TableData>{item.dateTime}</TableData>
                <TableData>{item.isPublished ? t('adminNewsPanel.yes') : t('adminNewsPanel.no')}</TableData>
                <TableData>{item.username}</TableData>
                <TableData>
                  <ButtonTable onClick={() => handleEdit(item)}>
                    <FaEdit />
                  </ButtonTable>
                  <ButtonTable onClick={() => handleDelete(item.id)}>
                    <FaTrash />
                  </ButtonTable>
                </TableData>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default AdminNewsPanel;
